import React, { memo, useMemo, useRef, useCallback, StrictMode, useState } from 'react';
import { defaultRangeExtractor, useVirtualizer } from '@tanstack/react-virtual';
import { Stack, Text, Icon, IconButton, Dropdown, IDropdownOption, IRenderFunction, useTheme, TooltipHost, DirectionalHint } from '@fluentui/react';
import { useGridLayoutContext } from './context/LayoutProvider';
import {
    useInitializeCorrectionsStore,
    useCorrectionsGridRenderableRows,
    TimeEntryCorrectionRow,
    CorrectionRow,
    TimeEntryRow,
    isTimeEntryRow,
    isCorrectionRow,
    useGridExpandedRowState,
    useCorrectionsStore,
} from '../../store/correctionsStore';
import { TimeEntryCorrectionData, CorrectionTimeEntryDto } from '../../../../api/generated/data-contracts';
import { format } from 'date-fns';
import shallow from 'zustand/shallow';
import { FilterDropdown } from '../../../ProjectApproval/components/FilterDropdown/FilterDropdown';
import { AddCorrectionModal } from '../AddCorrectionModal/AddCorrectionModal';

const ROW_HEIGHT = 60;
const ROW_STICKY_COUNT = 1;
const TOP_HEADERS_HEIGHT = 50;

export const CorrectionsGrid = memo(function CorrectionsGrid({ data }: { data: TimeEntryCorrectionData }) {
    const scrollRef = useRef<HTMLDivElement>(null);
    const activeStickyIndexRef = useRef(0);
    useGridLayoutContext();
    const [addCorrectionModal, setAddCorrectionModal] = useState<{
        isOpen: boolean;
        timeEntry?: CorrectionTimeEntryDto;
        timeEntryApprovalId?: string;
    }>({ isOpen: false });

    const allRows = useInitializeCorrectionsStore(data);
    const renderableRows = useCorrectionsGridRenderableRows(allRows);

    const stickyIndexes = useMemo(() => [0], []);

    // Calculate total virtual rows including correction headers
    const totalVirtualRows = useMemo(() => {
        let count = renderableRows.length + ROW_STICKY_COUNT;

        // Add extra rows for correction headers
        const expandedTimeEntries = new Set<string>();
        for (const row of renderableRows) {
            if (isCorrectionRow(row) && row.parentId && !expandedTimeEntries.has(row.parentId)) {
                expandedTimeEntries.add(row.parentId);
                count++; // Add one for the correction header
            }
        }

        return count;
    }, [renderableRows]);

    const rowVirtualizer = useVirtualizer({
        count: totalVirtualRows,
        getScrollElement: () => scrollRef.current,
        overscan: 5,
        estimateSize: useCallback((index: number) => {
            if (!index) {
                return TOP_HEADERS_HEIGHT;
            }
            return ROW_HEIGHT;
        }, []),
        rangeExtractor: useCallback(
            range => {
                activeStickyIndexRef.current = [...stickyIndexes].reverse().find(index => range.startIndex >= index);

                const next = new Set([activeStickyIndexRef.current, ...defaultRangeExtractor(range)]);

                return [...next].sort((a, b) => a - b);
            },
            [stickyIndexes],
        ),
    });

    const rows = rowVirtualizer.getVirtualItems();
    const totalHeight = rowVirtualizer.getTotalSize();

    const handleAddCorrection = useCallback((timeEntry: CorrectionTimeEntryDto) => {
        setAddCorrectionModal({
            isOpen: true,
            timeEntry,
            timeEntryApprovalId: timeEntry.timeEntryApprovalId,
        });
    }, []);

    // Create a mapping of virtual indices to actual content
    const virtualRowMapping = useMemo(() => {
        const mapping: Array<{ type: 'header' | 'correctionHeader' | 'row'; rowIndex?: number; parentId?: string }> = [];

        if (!renderableRows.length) {
            return mapping;
        }

        // Add main header
        mapping.push({ type: 'header' });

        // Track which time entries have had their correction headers added
        const correctionHeadersAdded = new Set<string>();

        for (let index = 0; index < renderableRows.length; index++) {
            const row = renderableRows[index];
            // If this is the first correction for a time entry, add correction header first
            if (isCorrectionRow(row) && row.parentId && !correctionHeadersAdded.has(row.parentId)) {
                mapping.push({ type: 'correctionHeader', parentId: row.parentId });
                correctionHeadersAdded.add(row.parentId);
            }

            // Add the actual row
            mapping.push({ type: 'row', rowIndex: index });
        }

        return mapping;
    }, [renderableRows]);

    const render = useMemo(() => {
        if (!renderableRows.length) {
            return (
                <>
                    <Headers />
                    <div style={{ padding: '20px', textAlign: 'center' }}>
                        <Text variant="medium">No time entry corrections found for this period</Text>
                    </div>
                </>
            );
        }

        return rows.map(({ index, start, key }) => {
            const mapping = virtualRowMapping[index];

            if (!mapping) {
                return null;
            }

            if (mapping.type === 'header') {
                return <Headers key={String(key)} start={start} />;
            }

            if (mapping.type === 'correctionHeader') {
                return <CorrectionHeaders key={String(key)} start={start} />;
            }

            if (mapping.type === 'row' && mapping.rowIndex !== undefined) {
                const row = renderableRows[mapping.rowIndex];
                return <RowRenderer key={String(key)} row={row} start={start} allRows={allRows} onAddCorrection={handleAddCorrection} />;
            }

            return null;
        });
    }, [renderableRows, rows, allRows, handleAddCorrection, virtualRowMapping]);

    const innerScrollContainerStyles = useMemo(
        (): React.CSSProperties => ({
            position: 'relative',
            width: '100%',
            height: `${totalHeight}px`,
        }),
        [totalHeight],
    );

    const handleCloseModal = useCallback(() => {
        setAddCorrectionModal({ isOpen: false });
    }, []);

    return (
        <StrictMode>
            <FilterBar />
            <div ref={scrollRef} style={scrollContainerStyle}>
                <div style={innerScrollContainerStyles}>{render}</div>
            </div>
            {addCorrectionModal.isOpen && addCorrectionModal.timeEntry && addCorrectionModal.timeEntryApprovalId && (
                <AddCorrectionModal
                    isOpen={addCorrectionModal.isOpen}
                    onDismiss={handleCloseModal}
                    timeEntry={addCorrectionModal.timeEntry}
                    timeEntryApprovalId={addCorrectionModal.timeEntryApprovalId}
                />
            )}
        </StrictMode>
    );
});

const scrollContainerStyle: React.CSSProperties = {
    height: 'calc(100% - 50px - 40px)', // Match ProjectApproval: filter bar + margin
    width: `100%`,
    overflow: 'auto',
    maxWidth: '100%',
};

const FilterBar = () => {
    const { selectedProjectIds, setSelectedProjectIds, selectedUserIds, setSelectedUserIds, selectedTaskIds, setSelectedTaskIds, projects, users, tasks } =
        useCorrectionsStore(
            store => ({
                selectedUserIds: store.selectedUserIds,
                setSelectedUserIds: store.setSelectedUserIds,
                selectedProjectIds: store.selectedProjectIds,
                setSelectedProjectIds: store.setSelectedProjectIds,
                selectedTaskIds: store.selectedTaskIds,
                setSelectedTaskIds: store.setSelectedTaskIds,
                projects: store.projects,
                users: store.users,
                tasks: store.tasks,
            }),
            shallow,
        );

    const projectFilterOptions = useMemo(
        (): IDropdownOption<any>[] =>
            Object.values(projects).map(project => {
                return {
                    key: project.id,
                    text: project.name,
                    data: project,
                };
            }),
        [projects],
    );

    const userFilterOptions = useMemo((): IDropdownOption<any>[] => {
        return Object.values(users).map(user => {
            return {
                key: user.id,
                text: user.name,
                data: user,
            };
        });
    }, [users]);

    const taskFilterOptions = useMemo((): IDropdownOption<any>[] => {
        return Object.values(tasks).map(task => {
            return {
                key: task.id,
                text: task.name,
                data: task,
            };
        });
    }, [tasks]);

    return (
        <Stack horizontal horizontalAlign="end" styles={{ root: { height: 40 } }}>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                <AllOptionsDropdown options={projectFilterOptions} setSelectedKeys={setSelectedProjectIds} selectedKeys={selectedProjectIds} />
                <AllOptionsDropdown options={userFilterOptions} setSelectedKeys={setSelectedUserIds} selectedKeys={selectedUserIds} />
                <AllOptionsDropdown options={taskFilterOptions} setSelectedKeys={setSelectedTaskIds} selectedKeys={selectedTaskIds} />
            </Stack>
        </Stack>
    );
};

const TOGGLE_ALL_KEY = 'TOGGLE_ALL_KEY';
const AllOptionsDropdown = ({
    options,
    selectedKeys,
    setSelectedKeys,
    mode = 'filter',
}: {
    options: IDropdownOption<any>[];
    selectedKeys: string[];
    setSelectedKeys: React.Dispatch<React.SetStateAction<string[]>>;
    mode?: 'default' | 'filter';
}) => {
    const allSelected = useMemo(() => options.length && options.every(option => selectedKeys.includes(option.key as string)), [options, selectedKeys]);

    const keys = useMemo(() => {
        const keys = [...selectedKeys];
        if (allSelected) {
            keys.unshift(TOGGLE_ALL_KEY);
        }
        return keys;
    }, [allSelected, selectedKeys]);

    const opts = useMemo((): IDropdownOption<any>[] => {
        return [
            {
                key: TOGGLE_ALL_KEY,
                text: allSelected ? 'Deselect all' : 'Select all',
            },
            ...options,
        ];
    }, [allSelected, options]);

    const onChange = useCallback(
        (e: any, opt?: IDropdownOption<any>) => {
            if (!opt) return;

            if (opt.selected) {
                if (opt.key === TOGGLE_ALL_KEY) {
                    setSelectedKeys(options.map(opt => opt.key as string));
                } else {
                    setSelectedKeys(states => [...states, opt.key as string]);
                }
            } else if (opt.key === TOGGLE_ALL_KEY) {
                setSelectedKeys([]);
            } else {
                setSelectedKeys(states => states.filter(state => state !== opt.key));
            }
        },
        [options, setSelectedKeys],
    );

    const DropdownComponent = useMemo(() => {
        if (mode === 'filter') {
            return FilterDropdown;
        }
        return Dropdown;
    }, [mode]);

    return (
        <DropdownComponent
            options={opts}
            onChange={onChange}
            selectedKeys={keys}
            multiSelect
            styles={{ root: { width: 180 } }}
            onRenderTitle={onRenderTitleWithDeselect}
        />
    );
};

const onRenderTitleWithDeselect: IRenderFunction<IDropdownOption<any>[]> = (props, defaultRender) => {
    return defaultRender(props.filter(opt => opt.key !== TOGGLE_ALL_KEY));
};

const Headers = ({ start = 0 }: { start?: number }) => {
    const theme = useTheme();
    const { gridRowStyles, totalWidth } = useGridLayoutContext();

    const styles = useMemo(() => {
        return {
            ...gridRowStyles,
            position: 'sticky',
            transform: `translateY(${start}px)`,
            height: TOP_HEADERS_HEIGHT,
            top: 0,
            left: 0,
            boxSizing: 'border-box',
            backgroundColor: theme.semanticColors.bodyBackground,
            zIndex: 9999,
            alignItems: 'center',
            borderBottom: `1px solid ${theme.semanticColors.disabledBackground}`,
            width: totalWidth,
        } as React.CSSProperties;
    }, [gridRowStyles, start, theme.semanticColors.bodyBackground, theme.semanticColors.disabledBackground, totalWidth]);

    return (
        <div style={styles}>
            <div></div> {/* Empty header for expand/collapse column */}
            <Text>
                <strong>Project / User / Task</strong>
            </Text>
            <Text>
                <strong>Registered Hours</strong>
            </Text>
            <Text>
                <strong>Date</strong>
            </Text>
            <Text>
                <strong>Actions</strong>
            </Text>
        </div>
    );
};

const CorrectionHeaders = ({ start }: { start: number }) => {
    const theme = useTheme();
    const { correctionTotalWidth, correctionGridColumnTemplate, gap } = useGridLayoutContext();

    const styles = useMemo(() => {
        return {
            position: 'absolute',
            top: start,
            left: 0,
            right: 0,
            height: ROW_HEIGHT,
            paddingLeft: 60, // Match correction row indent
            backgroundColor: theme.semanticColors.bodyBackgroundHovered,
            borderBottom: `1px solid ${theme.semanticColors.disabledBackground}`,
            alignItems: 'center',
            display: 'grid',
            gridTemplateColumns: correctionGridColumnTemplate,
            gap: gap,
            width: correctionTotalWidth,
            fontWeight: 600,
        } as React.CSSProperties;
    }, [start, theme.semanticColors.bodyBackgroundHovered, theme.semanticColors.disabledBackground, correctionTotalWidth, correctionGridColumnTemplate, gap]);

    return (
        <div style={styles}>
            <div></div> {/* Empty div for expand/collapse column */}
            <Text>
                <strong>Comment</strong>
            </Text>
            <Text>
                <TooltipHost
                    content="Change from last invoiced value"
                    directionalHint={DirectionalHint.topCenter}
                    styles={{ root: { display: 'inline-flex', alignItems: 'center', gap: 4 } }}
                >
                    <strong>Correction Hours</strong>
                    <Icon iconName="Info" styles={{ root: { fontSize: 12, color: '#605e5c' } }} />
                </TooltipHost>
            </Text>
            <Text>
                <strong>Date</strong>
            </Text>
            <Text>
                <strong>Invoice date</strong>
            </Text>
            <Text>
                <strong>Created By</strong>
            </Text>
            <div></div> {/* Empty div for padding column */}
        </div>
    );
};

const RowRenderer = ({
    row,
    start,
    allRows,
    onAddCorrection,
}: {
    row: TimeEntryCorrectionRow;
    start: number;
    allRows: TimeEntryCorrectionRow[];
    onAddCorrection?: (timeEntry: CorrectionTimeEntryDto) => void;
}) => {
    if (isTimeEntryRow(row)) {
        return <TimeEntryRowRenderer row={row} start={start} allRows={allRows} onAddCorrection={onAddCorrection} />;
    }

    if (isCorrectionRow(row)) {
        return <CorrectionRowRenderer row={row} start={start} allRows={allRows} />;
    }

    return null;
};

const TimeEntryRowRenderer = ({
    row,
    start,
    allRows,
    onAddCorrection,
}: {
    row: TimeEntryRow;
    start: number;
    allRows: TimeEntryCorrectionRow[];
    onAddCorrection?: (timeEntry: CorrectionTimeEntryDto) => void;
}) => {
    const theme = useTheme();
    const { gridRowStyles } = useGridLayoutContext();
    const [isExpanded, setIsExpanded] = useGridExpandedRowState(row.id);

    const hasChildren = allRows.some(r => r.parentId === row.id);
    const correctionCount = allRows.filter(r => r.parentId === row.id).length;

    const styles = useMemo(() => {
        return {
            ...gridRowStyles,
            position: 'absolute',
            top: start,
            left: 0,
            right: 0,
            height: ROW_HEIGHT,
            borderBottom: `1px solid ${theme.semanticColors.disabledBackground}`,
            backgroundColor: theme.semanticColors.bodyBackground,
            alignItems: 'center',
        } as React.CSSProperties;
    }, [gridRowStyles, start, theme.semanticColors.disabledBackground, theme.semanticColors.bodyBackground]);

    return (
        <div style={styles}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
                {hasChildren ? (
                    <IconButton
                        iconProps={{
                            iconName: 'ChevronRight',
                            style: {
                                color: theme.semanticColors.bodyText,
                                transform: isExpanded ? 'rotate(90deg)' : 'rotate(0deg)',
                                transition: 'transform 300ms',
                            },
                        }}
                        onClick={() => setIsExpanded(!isExpanded)}
                        styles={{
                            root: { minWidth: 24, width: 24, height: 24 },
                        }}
                    />
                ) : null}
            </div>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 10 }}>
                <Icon iconName="TimeEntry" styles={{ root: { fontSize: 16, color: theme.semanticColors.bodyText } }} />
                <Text>
                    <strong>{row.name}</strong>
                </Text>
            </Stack>
            <Text>{row.timeEntry.hours.toFixed(2)}</Text>
            <Text>{new Date(row.timeEntry.date).toLocaleDateString()}</Text>
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                <IconButton
                    iconProps={{ iconName: 'Add' }}
                    title="Add correction"
                    styles={{ root: { minWidth: 24, width: 24, height: 24 } }}
                    onClick={() => onAddCorrection?.(row.timeEntry)}
                />
                {correctionCount > 0 && (
                    <Text styles={{ root: { color: theme.semanticColors.bodySubtext } }}>
                        {correctionCount} correction{correctionCount === 1 ? '' : 's'}
                    </Text>
                )}
            </Stack>
        </div>
    );
};

const CorrectionRowRenderer = ({ row, start, allRows }: { row: CorrectionRow; start: number; allRows?: TimeEntryCorrectionRow[] }) => {
    const theme = useTheme();
    const { correctionTotalWidth, correctionGridColumnTemplate, gap } = useGridLayoutContext();
    const correction = row.correction;

    const getCorrectionHoursColor = useCallback(
        (hours: number) => {
            if (hours === 0) {
                return theme.semanticColors.bodyText;
            } else if (hours > 0) {
                return theme.palette.green;
            } else {
                return theme.palette.red;
            }
        },
        [theme],
    );

    const correctionHoursColor = getCorrectionHoursColor(correction.correctionHours);

    // Determine if this is the latest correction for this time entry
    const siblingCorrections = (allRows?.filter(r => r.parentId === row.parentId && isCorrectionRow(r)) as CorrectionRow[]) || [];
    const isLatestCorrection =
        siblingCorrections.length > 0 &&
        siblingCorrections.toSorted((a, b) => new Date(b.correction.created || 0).getTime() - new Date(a.correction.created || 0).getTime())[0]?.id === row.id;

    const styles = useMemo(() => {
        return {
            position: 'absolute',
            top: start,
            left: 0,
            right: 0,
            height: ROW_HEIGHT,
            borderBottom: `1px solid ${theme.semanticColors.disabledBackground}`,
            paddingLeft: 60, // Indent correction rows
            backgroundColor: theme.semanticColors.bodyBackgroundHovered,
            alignItems: 'center',
            display: 'grid',
            gridTemplateColumns: correctionGridColumnTemplate,
            gap: gap,
            width: correctionTotalWidth,
        } as React.CSSProperties;
    }, [start, theme.semanticColors.disabledBackground, theme.semanticColors.bodyBackgroundHovered, correctionTotalWidth, correctionGridColumnTemplate, gap]);

    return (
        <div style={styles}>
            <div></div> {/* Empty div for expand/collapse column */}
            <Stack horizontal verticalAlign="center" tokens={{ childrenGap: 8 }}>
                <Text title={correction.comment} styles={{ root: { color: theme.semanticColors.bodySubtext } }}>
                    {correction.comment.substring(0, 50)}
                    {correction.comment.length > 50 ? '...' : ''}
                </Text>
            </Stack>
            <Text styles={{ root: { color: correctionHoursColor, fontWeight: 600 } }}>
                {correction.correctionHours > 0 ? '+' : ''}
                {correction.correctionHours.toFixed(2)}
            </Text>
            <Text>{correction.created ? format(new Date(correction.created), 'MMM dd, yyyy') : '-'}</Text>
            <Text>{correction.invoiceDate ? format(new Date(correction.invoiceDate), 'MMM dd, yyyy') : '-'}</Text>
            <Text title={correction.createdBy?.principalName}>{correction.createdBy?.name || '-'}</Text>
            <div></div> {/* Empty div for padding column */}
        </div>
    );
};
